import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, idempotencyKey, unwrap } from '@/api/client'
import type { Order, OrderStatus, RiskLevel, Schemas } from '@/api/types'

export interface OrderFilters {
  status?: OrderStatus
  risk?: RiskLevel
}

export const orderKeys = {
  all: ['orders'] as const,
  list: (f: OrderFilters) => ['orders', 'list', f] as const,
  detail: (id: string) => ['orders', 'detail', id] as const,
  stages: (id: string) => ['orders', 'stages', id] as const,
  history: (id: string) => ['orders', 'history', id] as const,
}

export function useOrders(filters: OrderFilters) {
  return useQuery({
    queryKey: orderKeys.list(filters),
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/orders', {
          params: { query: { status: filters.status, risk: filters.risk } },
        }),
      ),
  })
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: orderKeys.detail(id),
    queryFn: () => unwrap(api.GET('/api/v1/orders/{id}', { params: { path: { id } } })),
  })
}

export function useOrderStages(id: string) {
  return useQuery({
    queryKey: orderKeys.stages(id),
    queryFn: () => unwrap(api.GET('/api/v1/orders/{id}/stages', { params: { path: { id } } })),
  })
}

export function useOrderHistory(id: string) {
  return useQuery({
    queryKey: orderKeys.history(id),
    queryFn: () => unwrap(api.GET('/api/v1/orders/{id}/history', { params: { path: { id } } })),
  })
}

export function useCreateOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Schemas['OrderCreateRequest']) =>
      unwrap(
        api.POST('/api/v1/orders', {
          body,
          params: { header: { 'Idempotency-Key': idempotencyKey() } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: orderKeys.all }),
  })
}

export function useUpdateOrder(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Schemas['OrderUpdateRequest']) =>
      unwrap(api.PATCH('/api/v1/orders/{id}', { body, params: { path: { id } } })),
    onSuccess: (order) => {
      qc.setQueryData(orderKeys.detail(id), order)
      void qc.invalidateQueries({ queryKey: orderKeys.all })
    },
  })
}

export type LifecycleAction = 'submit' | 'launch' | 'pause' | 'resume' | 'ship' | 'return-to-draft'

const LIFECYCLE_PATHS = {
  submit: '/api/v1/orders/{id}/submit',
  launch: '/api/v1/orders/{id}/launch',
  pause: '/api/v1/orders/{id}/pause',
  resume: '/api/v1/orders/{id}/resume',
  ship: '/api/v1/orders/{id}/ship',
  'return-to-draft': '/api/v1/orders/{id}/return-to-draft',
} as const

/** Generic lifecycle transition (no body) with idempotency + cache refresh. */
export function useOrderLifecycle(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (action: LifecycleAction) =>
      unwrap(
        api.POST(LIFECYCLE_PATHS[action], {
          params: { path: { id }, header: { 'Idempotency-Key': idempotencyKey() } },
        }),
      ),
    onSuccess: (order: Order) => {
      qc.setQueryData(orderKeys.detail(id), order)
      void qc.invalidateQueries({ queryKey: orderKeys.all })
      void qc.invalidateQueries({ queryKey: orderKeys.stages(id) })
      void qc.invalidateQueries({ queryKey: orderKeys.history(id) })
    },
  })
}

export function useCancelOrder(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        api.POST('/api/v1/orders/{id}/cancel', {
          body: { reason },
          params: { path: { id }, header: { 'Idempotency-Key': idempotencyKey() } },
        }),
      ),
    onSuccess: (order) => {
      qc.setQueryData(orderKeys.detail(id), order)
      void qc.invalidateQueries({ queryKey: orderKeys.all })
      void qc.invalidateQueries({ queryKey: orderKeys.stages(id) })
    },
  })
}

export function useCreateOrderQR(id: string) {
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/orders/{id}/qr', { params: { path: { id } } })),
  })
}
